import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Building2, Briefcase, BarChart3, Settings, LogOut, Menu, X,
  Users, Shield, Bot, Activity, Mail, Lock, BarChart as ChartBar,
  Brain, Inbox,
  LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ClaudeTokenStats } from '@/components/ClaudeTokenStats';
import { useAdminRole } from '@/hooks/useAdminRole';
import { usePermissions } from '@/hooks/usePermissions';
import { canViewClaudeStats } from '@/lib/permissions';
import { AdminPermissions } from '@/types';

interface MenuItem {
  icon: LucideIcon;
  label: string;
  href: string;
  permission?: keyof AdminPermissions;
  founderOnly?: boolean;
}

const ADMIN_MENU_ITEMS: MenuItem[] = [
  { icon: Users, label: 'Customers', href: '/admin/customers', permission: 'manageUsers' },
  { icon: ChartBar, label: 'Statistics', href: '/admin/statistics', permission: 'viewAnalytics' },
  { icon: Activity, label: 'System Health', href: '/admin/health', permission: 'viewHealth' },
  { icon: Bot, label: 'Bot Config', href: '/admin/bot-config', founderOnly: true },
  { icon: Brain, label: 'Claude Stats', href: '/admin/claude-stats', founderOnly: true },
  { icon: Mail, label: 'Email Templates', href: '/admin/email-templates', permission: 'manageSystem' },
  { icon: Lock, label: 'Permissions', href: '/admin/permissions', permission: 'manageAdmins' }
];

const DASHBOARD_MENU_ITEMS: MenuItem[] = [
  { icon: Building2, label: 'Company', href: '/dashboard/company' },
  { icon: Briefcase, label: 'Jobs', href: '/dashboard/jobs' },
  { icon: BarChart3, label: 'Analytics', href: '/dashboard/analytics' },
  { icon: Inbox, label: 'Applications', href: '/dashboard/applications' },
  { icon: Settings, label: 'Settings', href: '/dashboard/settings' }
];

export function DashboardLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { adminRole, loading: roleLoading } = useAdminRole();
  const { permissions, loading: permissionsLoading } = usePermissions(adminRole);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAccess();
  }, [location.pathname, adminRole]);

  const checkAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (!profile) {
        navigate('/login');
        return;
      }

      if (profile.role !== 'admin') {
        navigate('/dashboard');
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error('Error checking access:', error);
      navigate('/login');
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const getVisibleMenuItems = () => {
    const isAdminRoute = location.pathname.startsWith('/admin');

    if (isAdminRoute) {
      // Founders see all menu items
      if (adminRole === 'founder') {
        return ADMIN_MENU_ITEMS;
      }

      // Non-founders only see items they have permission for
      return ADMIN_MENU_ITEMS.filter(item => {
        if (item.founderOnly) {
          return false;
        }
        if (item.permission) {
          return permissions?.[item.permission];
        }
        return true;
      });
    }

    const items = [...DASHBOARD_MENU_ITEMS];
    if (adminRole) {
      items.push({ icon: Shield, label: 'Admin Panel', href: '/admin' });
    }
    return items;
  };

  if (roleLoading || permissionsLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-lg">Loading...</div>
      </div>
    );
  }

  const menuItems = getVisibleMenuItems();

  return (
    <div className="min-h-screen bg-background">
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? <X /> : <Menu />}
      </Button>

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 bg-card border-r transform transition-transform duration-200 ease-in-out lg:translate-x-0",
          !isSidebarOpen && "-translate-x-full"
        )}
      >
        <div className="h-full px-3 py-4 flex flex-col">
          <div className="mb-8 px-4 py-3 flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-bold">Job AI</h1>
              {adminRole && (
                <Badge variant="secondary" className="text-xs">
                  {adminRole.toUpperCase()}
                </Badge>
              )}
            </div>
            <ThemeToggle />
          </div>

          {/* Claude Token Stats for founders only */}
          {canViewClaudeStats(adminRole) && (
            <div className="mb-6 px-4">
              <ClaudeTokenStats />
            </div>
          )}

          <nav className="flex-1 space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.href || 
                             (item.href === '/admin' && location.pathname.startsWith('/admin/'));
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center px-4 py-2 text-sm rounded-md transition-colors",
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "text-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="mt-auto border-t pt-4">
            <Button
              variant="ghost"
              className="w-full justify-start text-foreground"
              onClick={handleSignOut}
            >
              <LogOut className="mr-3 h-5 w-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      <main className={cn(
        "transition-all duration-200 ease-in-out",
        isSidebarOpen ? "lg:ml-64" : ""
      )}>
        <div className="container mx-auto px-4 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}